import { Navigate } from "react-router";
import { useAuthStore } from "../../stores/authStore";
import { isAppSubdomain } from "../../lib/subdomain";
import { StatCard } from "../../components/shared/StatCard";
import { PageHeader } from "../../components/shared/PageHeader";
import { CheckCircle, XCircle } from "lucide-react";
import { useEnterpriseDashboard, useApproveUser, useDeclineUser } from "@platform/hooks";
import { useToast } from "@nthamizh/ui";

interface AuditEntry {
  id: string;
  action: string;
  actorEmail: string | null;
  createdAt: string;
}

interface PendingUser {
  id: string;
  name: string;
  email: string;
  requestedAt: string;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function actionLabel(action: string): string {
  return action.replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

export function EnterpriseDashboardPage() {
  const user = useAuthStore((s) => s.user);

  // This page's query is enterprise-scoped — on app.<domain> no tenant
  // resolves and it is guaranteed to 400. Platform users reaching it
  // there (old bookmarks, manual URL, pre-fix redirects) get sent to a
  // page that works instead: admins to their own dashboard; support to
  // /profile, because /platform is AdminRoute-gated and would bounce
  // them straight back here in a loop. Mirrors resolvePostLoginUrl.
  const isPlatformAdmin = user?.platformRole === "platform_admin";
  const isSupport = user?.platformRole === "support";
  if (isAppSubdomain() && isPlatformAdmin) return <Navigate to="/platform" replace />;
  if (isAppSubdomain() && isSupport) return <Navigate to="/profile" replace />;

  return <EnterpriseDashboardContent />;
}

function EnterpriseDashboardContent() {
  const user = useAuthStore((s) => s.user);
  const { data, isLoading } = useEnterpriseDashboard();
  const approve = useApproveUser();
  const decline = useDeclineUser();
  const { toast } = useToast();

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  const pendingUsers = (data?.pendingUsers ?? []) as PendingUser[];
  const recentActivity = (data?.recentActivity ?? []) as AuditEntry[];

  const handleApprove = async (userId: string, name: string) => {
    try {
      await approve.mutateAsync(userId);
      toast({ title: `${name} approved`, variant: "success" });
    } catch {
      toast({ title: "Failed to approve user", variant: "error" });
    }
  };

  const handleDecline = async (userId: string, name: string) => {
    try {
      await decline.mutateAsync(userId);
      toast({ title: `${name} declined`, variant: "success" });
    } catch {
      toast({ title: "Failed to decline user", variant: "error" });
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={`${greeting}, ${user?.name?.split(" ")[0]}`}
        subtitle="Here's what's happening in your enterprise today."
      />

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Total users" value={data?.total ?? "—"} />
        <StatCard label="Active" value={data?.active ?? "—"} deltaType="positive" />
        <StatCard
          label="Pending approvals"
          value={data?.pending ?? "—"}
          deltaType={(data?.pending ?? 0) > 0 ? "warning" : "neutral"}
          delta={(data?.pending ?? 0) > 0 ? "Needs review" : undefined}
        />
        <StatCard label="Apps enabled" value="—" />
      </div>

      {/* Pending approvals */}
      {pendingUsers.length > 0 && (
        <div className="bg-white border border-ds-border rounded-card shadow-card">
          <div className="px-5 py-4 border-b border-ds-border">
            <h2 className="text-sm font-semibold text-ds-text">Pending user approvals</h2>
          </div>
          <div className="divide-y divide-ds-border">
            {pendingUsers.map((u) => (
              <div key={u.id} className="flex items-center gap-4 px-5 py-3.5">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-xs font-semibold flex-shrink-0">
                  {u.name.slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ds-text truncate">{u.name}</p>
                  <p className="text-xs text-ds-text-muted truncate">
                    {u.email} · Requested {timeAgo(u.requestedAt)}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleApprove(u.id, u.name)}
                    disabled={approve.isPending}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-xs font-medium disabled:opacity-50 transition-colors"
                  >
                    <CheckCircle className="w-3.5 h-3.5" /> Approve
                  </button>
                  <button
                    onClick={() => handleDecline(u.id, u.name)}
                    disabled={decline.isPending}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 text-xs font-medium disabled:opacity-50 transition-colors"
                  >
                    <XCircle className="w-3.5 h-3.5" /> Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent activity */}
      <div className="bg-white border border-ds-border rounded-card shadow-card">
        <div className="px-5 py-4 border-b border-ds-border">
          <h2 className="text-sm font-semibold text-ds-text">Recent activity</h2>
        </div>
        {isLoading ? (
          <div className="px-5 py-8 text-center text-sm text-ds-text-muted">Loading activity...</div>
        ) : recentActivity.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-ds-text-muted">No activity yet.</div>
        ) : (
          <div className="divide-y divide-ds-border">
            {recentActivity.map((a) => (
              <div key={a.id} className="flex items-center gap-4 px-5 py-3">
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                <p className="text-sm text-ds-text flex-1">{actionLabel(a.action)}</p>
                <p className="text-xs text-ds-text-muted">{a.actorEmail ?? "System"}</p>
                <p className="text-xs text-ds-text-muted w-16 text-right">{timeAgo(a.createdAt)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
